<div class="chat-page" [class.has-conversation]="selectedConversation">
  <!-- Conversations Sidebar -->
  <div class="conversations-sidebar">
    <div class="sidebar-header">
      <h2>{{ 'chat.messages' | translate }}</h2>
    </div>

    <div class="conversations-list">
      @if (isLoadingConversations) {
        <div class="loading-state">
          <i class="fa fa-spinner fa-spin"></i>
          <span>{{ 'chat.loadingConversations' | translate }}</span>
        </div>
      } @else if (conversations.length === 0) {
        <div class="empty-state">
          <i class="fa fa-inbox"></i>
          <span>{{ 'chat.noConversationsYet' | translate }}</span>
          <p>{{ 'chat.startConversationHint' | translate }}</p>
        </div>
      } @else {
        @for (conversation of conversations; track conversation.conversation_id) {
          <div
            class="conversation-item"
            [class.selected]="selectedConversation?.conversation_id === conversation.conversation_id"
            [class.unread]="conversation.unread_count > 0"
            (click)="selectConversation(conversation)"
          >
            <div class="conversation-avatar" [ngClass]="'context-' + getConversationContext(conversation)">
              <i class="fa" [ngClass]="getContextIcon(conversation)"></i>
            </div>
            <div class="conversation-info">
              <div class="conversation-header">
                <span class="conversation-title">{{ getConversationTitle(conversation) }}</span>
                <span class="conversation-time">{{ getTimeAgo(conversation.updated_at) }}</span>
              </div>
              <div class="conversation-context">
                <span class="context-badge" [ngClass]="'context-' + getConversationContext(conversation)">
                  <i class="fa" [ngClass]="getContextIcon(conversation)"></i>
                  {{ getContextLabel(conversation) | translate }}
                </span>
                @if (getOtherParticipants(conversation).length > 0) {
                  <span class="participant-count">
                    <i class="fa fa-users"></i>
                    {{ getOtherParticipants(conversation).length + 1 }}
                  </span>
                }
              </div>
              <div class="conversation-preview">
                {{ getLastMessagePreview(conversation) }}
                @if (conversation.unread_count > 0) {
                  <span class="unread-badge">{{ conversation.unread_count }}</span>
                }
              </div>
            </div>
          </div>
        }
      }
    </div>
  </div>

  <!-- Chat Area -->
  <div class="chat-area">
    @if (!selectedConversation) {
      <div class="no-conversation-selected">
        <i class="fa fa-comments"></i>
        <h3>{{ 'chat.selectConversation' | translate }}</h3>
        <p>{{ 'chat.selectConversationHint' | translate }}</p>
      </div>
    } @else {
      <!-- Chat Header -->
      <div class="chat-header">
        <button class="back-btn mobile-only" (click)="goBackToList()">
          <i class="fa fa-arrow-left"></i>
        </button>
        <div class="chat-header-info">
          <div class="chat-avatar" [ngClass]="'context-' + getConversationContext(selectedConversation)">
            <i class="fa" [ngClass]="getContextIcon(selectedConversation)"></i>
          </div>
          <div class="chat-title-info">
            <h3>{{ getConversationTitle(selectedConversation) }}</h3>
            <div class="chat-meta">
              <span class="context-badge-header" [ngClass]="'context-' + getConversationContext(selectedConversation)">
                <i class="fa" [ngClass]="getContextIcon(selectedConversation)"></i>
                {{ getContextLabel(selectedConversation) | translate }}
              </span>
              @if (selectedConversation.event) {
                <span class="meta-separator">•</span>
                <span class="meta-info">
                  <i class="fa fa-calendar"></i>
                  {{ selectedConversation.event.event_name }}
                </span>
              }
              @if (selectedConversation.company) {
                <span class="meta-separator">•</span>
                <span class="meta-info">
                  <i class="fa fa-building"></i>
                  {{ selectedConversation.company.company_name }}
                </span>
              }
            </div>
          </div>
        </div>
      </div>

      <!-- Participants Bar -->
      @if (selectedConversation.participants && selectedConversation.participants.length > 0) {
        <div class="participants-bar">
          <div class="participants-label">
            <i class="fa fa-users"></i>
            <span>{{ 'chat.participants' | translate }} ({{ selectedConversation.participants.length }})</span>
          </div>
          <div class="participants-list">
            @for (participant of selectedConversation.participants; track participant.participant_id) {
              <div class="participant-chip" [class.is-you]="participant.user_id.toString() === currentUserId">
                <span class="participant-avatar">{{ getParticipantInitials(participant) }}</span>
                <span class="participant-name">
                  {{ getParticipantName(participant) }}
                  @if (participant.user_id.toString() === currentUserId) {
                    <span class="you-badge">{{ 'chat.you' | translate }}</span>
                  }
                </span>
              </div>
            }
          </div>
        </div>
      }

      <!-- Messages Area -->
      <div class="messages-area" #messagesContainer>
        @if (isLoadingMessages) {
          <div class="loading-state">
            <i class="fa fa-spinner fa-spin"></i>
            <span>{{ 'chat.loadingMessages' | translate }}</span>
          </div>
        } @else if (messages.length === 0) {
          <div class="empty-messages">
            <i class="fa fa-comment"></i>
            <span>{{ 'chat.noMessagesYet' | translate }}</span>
            <p>{{ 'chat.sendFirstMessage' | translate }}</p>
          </div>
        } @else {
          @for (message of messages; track message.message_id) {
            <div class="message" [class.own]="isOwnMessage(message)">
              @if (!isOwnMessage(message)) {
                <div class="message-avatar">
                  <i class="fa fa-user-circle"></i>
                </div>
              }
              <div class="message-content">
                @if (!isOwnMessage(message)) {
                  <div class="message-sender">{{ getSenderName(message) }}</div>
                }
                <div class="message-bubble">
                  {{ message.message_text }}
                </div>
                <div class="message-time">{{ formatMessageTime(message.created_at) }}</div>
              </div>
            </div>
          }
        }
      </div>

      <!-- Message Input -->
      <div class="message-input-area">
        <div class="message-input-wrapper">
          <textarea
            [(ngModel)]="newMessage"
            (keypress)="onKeyPress($event)"
            [placeholder]="'chat.typeMessage' | translate"
            rows="1"
            [disabled]="isSending"
          ></textarea>
          <button
            class="send-btn"
            (click)="sendMessage()"
            [disabled]="!newMessage.trim() || isSending"
          >
            @if (isSending) {
              <i class="fa fa-spinner fa-spin"></i>
            } @else {
              <i class="fa fa-paper-plane"></i>
            }
          </button>
        </div>
      </div>
    }
  </div>
</div>
